import { API_BASE } from '../config';
import type {
    CombineOnItem,
    EvaluationResultRow,
    MeepMeepJobResponse,
    QuantArcBacktestJobResponse,
    TranslationReport,
} from '../types/Api';
import type { StrategyNode } from '../types/BaseNode';
import { VarianceNode } from '../types/VarianceNode';
import { getAccessToken } from '../core/auth/tokenUtil';

/**
 * Centralized API Service
 * All backend communication goes through this service for consistency and error handling.
 */

export class ApiService {
    private static baseUrl = API_BASE;

    private static toUrl(path: string): URL {
        const fullPath = `${this.baseUrl}${path}`;
        const isAbsolute = /^https?:\/\//i.test(fullPath);
        return isAbsolute ? new URL(fullPath) : new URL(fullPath, window.location.origin);
    }

    /**
     * Generic fetch wrapper with error handling
     */
    private static async fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
        const mergedHeaders = new Headers(options?.headers ?? {});
        if (!mergedHeaders.has('Content-Type') && options?.body) {
            mergedHeaders.set('Content-Type', 'application/json');
        }
        if (!mergedHeaders.has('Authorization')) {
            const token = await getAccessToken();
            mergedHeaders.set('Authorization', `Bearer ${token}`);
        }

        const response = await fetch(url, {
            ...options,
            headers: mergedHeaders,
            credentials: 'include',
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        return response.json();
    }

    /**
     * Evaluate a strategy tree
     * Note: This endpoint returns structured errors even on 500 status
     */
    static async evaluateStrategy(
        serializedTree: unknown,
        eval_context?: { start_date: string | null; end_date: string | null },
        rootNode?: StrategyNode | null
    ): Promise<any> {
        // Embed the context into the root of the serialized tree object
        const payload = eval_context
            ? { ...(serializedTree as object), context: eval_context }
            : serializedTree;

        const response = await fetch(`${this.baseUrl}/evaluate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            credentials: 'include',
        });

        // Always parse JSON, even on error responses
        const json = await response.json();

        // ✅ Data Normalization: Enrich insight results if possible
        if (json.success !== false && json.nature === 'insight' && rootNode) {
            return this.enrichInsightResult(json, rootNode);
        }

        return json;
    }

    /**
     * Translate a strategy tree to QuantArc Python code
     */
    static async translateStrategy(
        serializedTree: unknown,
        eval_context?: { start_date: string | null; end_date: string | null },
    ): Promise<{
        success: boolean;
        filename?: string;
        content?: string;
        bundle_filename?: string;
        bundle_content_base64?: string;
        support_version?: string;
        message?: string;
        error?: string;
        details?: string[];
        translation_report?: TranslationReport;
    }> {
        const payload = eval_context
            ? { ...(serializedTree as object), context: eval_context }
            : serializedTree;

        const response = await fetch(`${this.baseUrl}/translate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${await getAccessToken()}`
            },
            body: JSON.stringify(payload),
            credentials: 'include',
        });

        return response.json();
    }

    /**
     * Run a translated PnL tree through the QuantArc backtest endpoint via the backend.
     */
    static async runQuantArcBacktest(
        serializedTree: unknown,
        eval_context?: { start_date: string | null; end_date: string | null },
    ): Promise<{
        success: boolean;
        nature?: string;
        data?: any[];
        chart_data?: any[];
        chart_series_labels?: Record<string, string>;
        stats?: Record<string, number | null>;
        engine?: string;
        strategy_name?: string;
        warnings?: string[];
        errors?: string[];
        error?: string;
        message?: string;
        translation_report?: TranslationReport;
    }> {
        const payload = eval_context
            ? { ...(serializedTree as object), context: eval_context }
            : serializedTree;

        const response = await fetch(`${this.baseUrl}/translate/backtest`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${await getAccessToken()}`
            },
            body: JSON.stringify(payload),
            credentials: 'include',
        });

        return response.json();
    }

    static async startQuantArcBacktestJob(
        serializedTree: unknown,
        eval_context?: { start_date: string | null; end_date: string | null },
    ): Promise<QuantArcBacktestJobResponse> {
        const payload = eval_context
            ? { ...(serializedTree as object), context: eval_context }
            : serializedTree;

        return this.fetchJson<QuantArcBacktestJobResponse>(`${this.baseUrl}/translate/backtest/jobs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${await getAccessToken()}`
            },
            body: JSON.stringify(payload),
        });
    }

    static async getQuantArcBacktestJob(jobId: string): Promise<QuantArcBacktestJobResponse> {
        return this.fetchJson<QuantArcBacktestJobResponse>(`${this.baseUrl}/translate/backtest/jobs/${jobId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${await getAccessToken()}`
            },
        });
    }

    static async cancelQuantArcBacktestJob(jobId: string): Promise<QuantArcBacktestJobResponse> {
        return this.fetchJson<QuantArcBacktestJobResponse>(`${this.baseUrl}/translate/backtest/jobs/${jobId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${await getAccessToken()}`
            },
        });
    }

    static cancelQuantArcBacktestJobKeepalive(jobId: string, accessToken: string): void {
        void fetch(`${this.baseUrl}/translate/backtest/jobs/${jobId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            keepalive: true,
            credentials: 'include',
        }).catch(() => {
            // Browser close cancellation is best-effort only.
        });
    }

    /**
     * Enriches the backend's "insight" results with human-readable titles from the strategy node graph.
     * This is primarily needed for the Variance/Attribution node where the backend
     * returns generic labels like 'param_1', 'param_2', etc.
     */
    private static enrichInsightResult(result: any, rootNode: StrategyNode): any {
        if (!result || rootNode.type !== 'Variance') {
            return result;
        }

        const varianceNode = rootNode as VarianceNode;
        const parameters = varianceNode.config.parameters ?? [];
        const targetNode = varianceNode.config.target;

        // Create a mapping from generic 'param_N' to node titles
        const labelMapping: Record<string, string> = {};
        parameters.forEach((p, i) => {
            labelMapping[`param_${i + 1}`] = p.title || p.type;
        });

        const enriched = { ...result };

        // 1. Map results list
        if (Array.isArray(enriched.results)) {
            enriched.results = enriched.results.map((r: any) => ({
                ...r,
                label: labelMapping[r.label] || r.label
            }));
        }

        // 2. Map explanation text
        if (typeof enriched.explanation === 'string') {
            let text = enriched.explanation;
            Object.entries(labelMapping).forEach(([generic, actual]) => {
                const regex = new RegExp(generic.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'g');
                text = text.replace(regex, actual);
            });
            enriched.explanation = text;
        }

        // 3. Map rolling correlations
        if (Array.isArray(enriched.rolling_correlations)) {
            enriched.rolling_correlations = enriched.rolling_correlations.map((s: any) => ({
                ...s,
                label: labelMapping[s.label] || s.label
            }));
        }

        // 4. Map target label
        if (enriched.target_label === 'target' && targetNode) {
            enriched.target_label = targetNode.title || targetNode.type;
        }

        return enriched;
    }

    /**
     * Health check / heartbeat
     */
    static async getHeartbeat(): Promise<{ status: string }> {
        return this.fetchJson(`${this.baseUrl}/health/heartbeat`);
    }

    /**
     * Get currently active alerts
     */
    static async getCurrentlyActiveAlerts(domainId?: string): Promise<{ data: unknown[] }> {
        const url = this.toUrl('/alerts/currently-active');
        if (domainId) url.searchParams.append('domain_id', domainId);
        return this.fetchJson(url.toString());
    }

    /**
     * Get all active alerts (legacy endpoint)
     */
    static async getActiveAlerts(): Promise<{ data: unknown[] }> {
        return this.fetchJson(`${this.baseUrl}/alerts/active`);
    }

    /**
     * Get monitored alerts
     */
    static async getMonitoredAlerts(domainId?: string): Promise<{ data: unknown[] }> {
        const url = this.toUrl('/alerts/monitored');
        if (domainId) url.searchParams.append('domain_id', domainId);
        return this.fetchJson(url.toString());
    }

    /**
     * Delete an alert by ID
     */
    static async deleteAlert(id: string): Promise<void> {
        await fetch(`${this.baseUrl}/alerts/${id}`, { method: 'DELETE' });
    }

    /**
     * Fetch a single monitored strategy by ID (used for deep linking)
     */
    static async getStrategyById(id: number): Promise<{ data: { StrategyJson: string; ConditionJson: string; Name: string; ID: number } }> {
        return this.fetchJson(`${this.baseUrl}/alerts/${id}`);
    }

    /**
     * Update an alert
     */
    static async updateAlert(id: string, payload: unknown, domainId?: string): Promise<unknown> {
        const url = this.toUrl(`/alerts/${id}`);
        if (domainId) url.searchParams.append('domain_id', domainId);
        return this.fetchJson(url.toString(), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
    }

    /**
     * Update an alert's logic (JSON)
     */
    static async updateAlertLogic(id: string, payload: unknown, domainId?: string): Promise<unknown> {
        const url = this.toUrl(`/alerts/${id}/logic`);
        if (domainId) url.searchParams.append('domain_id', domainId);
        return this.fetchJson(url.toString(), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
    }

    /**
     * Create a new alert
     */
    static async createAlert(payload: unknown, domainId?: string): Promise<unknown> {
        const url = this.toUrl('/alerts');
        if (domainId) url.searchParams.append('domain_id', domainId);
        return this.fetchJson(url.toString(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
    }

    /**
     * Generate AI strategy
     */
    static async generateAiStrategy(payload: { prompt: string }): Promise<unknown> {
        return this.fetchJson(`${this.baseUrl}/strategy/ai/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
    }

    /**
     * Calculate overlay
     */
    static async calculateOverlay(payload: {
        type_id: string;
        params: Record<string, unknown>;
        data: EvaluationResultRow[];
    }): Promise<{
        columns: { id: string; type?: string }[];
        data: Record<string, unknown>[];
        display?: unknown;
    }> {
        return this.fetchJson(`${this.baseUrl}/overlay/calculate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
    }

    /**
     * Get supported combine_on options from the backend.
     */
    static async getCombineOnOptions(): Promise<{ combine_on: CombineOnItem[] }> {
        return this.fetchJson(`${this.baseUrl}/combine_on`);
    }

    /**
     * Run MeepMeep calibration fit.
     * Aligned with docs/meepmeepintegration/calibration_plan.md
     */
    static async calibrateMeepMeep(payload: {
        strategy: unknown;
        calibration: {
            regression_node_id: string;
            train_start: string;
            train_end: string;
            validation_start?: string;
            validation_end?: string;
            target: {
                type: string;
                horizon_seconds: number;
                market_source: string;
            };
            model: {
                type: 'ridge';
                alpha: number;
                fit_intercept: boolean;
            };
            sample_weighting?: {
                type: 'ema_halflife' | 'none';
                halflife_events: number;
            };
            normalization?: {
                features: 'zscore_train_only' | 'none';
                target: 'none';
            };
        }
    }): Promise<{
        success: boolean;
        artifact?: any;
        apply_patch: {
            regression_node_id: string;
            weights: number[];
            model_type: string;
        };
        error?: string;
        details?: string[];
    }> {
        return this.fetchJson(`${this.baseUrl}/meepmeep/calibrate`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    static async verifyMeepMeepParity(payload: {
        strategy: unknown;
        reference: {
            data: Record<string, unknown>[];
            kind?: string;
            value_atol?: number;
            min_equity_correlation?: number;
            time_tolerance_ms?: number;
        };
        context?: { start_date: string | null; end_date: string | null };
    }): Promise<{
        success: boolean;
        parity_report?: any;
        error?: string;
    }> {
        return this.fetchJson(`${this.baseUrl}/meepmeep/verify`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    static async exportMeepMeep(payload: {
        strategy: unknown;
        parity_report: unknown;
        calibration_artifact?: unknown;
        calibration_artifact_path?: string;
    }): Promise<{
        success: boolean;
        config?: any;
        message?: string;
        error?: string;
        reasons?: string[];
        manifest?: unknown;
    }> {
        return this.fetchJson(`${this.baseUrl}/meepmeep/export`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    static async getMeepMeepDailyDetail(payload: {
        strategy: unknown;
        date: string;
        include_detail_charts: boolean;
        include_trade_log: boolean;
        context?: any;
    }): Promise<{
        success: boolean;
        nature: string;
        date: string;
        detail_chart_data?: any[];
        trade_log?: any[];
        chart_series_labels?: Record<string, string>;
    }> {
        return this.fetchJson(`${this.baseUrl}/meepmeep/daily-detail`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    static async startMeepMeepJob(
        serializedTree: unknown,
        eval_context?: { start_date: string | null; end_date: string | null },
    ): Promise<MeepMeepJobResponse> {
        const payload = eval_context
            ? { ...(serializedTree as object), context: eval_context }
            : serializedTree;

        return this.fetchJson<MeepMeepJobResponse>(`${this.baseUrl}/meepmeep/jobs`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    static async getMeepMeepJob(jobId: string): Promise<MeepMeepJobResponse> {
        return this.fetchJson<MeepMeepJobResponse>(`${this.baseUrl}/meepmeep/jobs/${jobId}`, {
            method: 'GET',
        });
    }

    static async cancelMeepMeepJob(jobId: string): Promise<MeepMeepJobResponse> {
        return this.fetchJson<MeepMeepJobResponse>(`${this.baseUrl}/meepmeep/jobs/${jobId}`, {
            method: 'DELETE',
        });
    }
}

